import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Flex,
  Group,
  Pagination,
  Skeleton,
  Table,
  Tabs,
  Text
} from '@mantine/core';
import { IconExternalLink, IconInfoCircle, IconPlus } from '@tabler/icons-react';

import { useGetOrgByIdQuery } from '@/store/slices/api/organizationApiSlice';
import {
  useGetPatientQuery,
  useGetPatientSchedulesQuery
} from '@/store/slices/api/patientsApiSlice';
import formatDate from '@/utils/formatDate';
import withErrorBoundary from '@/utils/withErrorBoundary';
import InfoMessage from '@/components/InfoMessage';
import { routes } from '@/routes';
import EditMenuIcon from '@/assets/icons/EditMenuIcon';
import PrintIcon from '@/assets/icons/PrintIcon';

import { Address } from '@/types/organization';
import { PatientSchedule } from '@/types/patient';

import DeviceScheduleModal from '../../DeviceScheduleModal';

import styles from './ScheduleList.module.scss';
import PrintScheduleIndex from './SchedulePrint';

// Storage key for the display of infobar about processing schedules
const PROCESSING_SCHEDULES_ALERT_KEY = 'schedule_processing_alert';
const ALERT_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const setProcessingAlertTimestamp = () => {
  sessionStorage.setItem(PROCESSING_SCHEDULES_ALERT_KEY, Date.now().toString());
};

const getProcessingAlertTimestamp = (): number | null => {
  const timestamp = sessionStorage.getItem(PROCESSING_SCHEDULES_ALERT_KEY);
  return timestamp ? parseInt(timestamp, 10) : null;
};

const clearProcessingAlert = () => {
  sessionStorage.removeItem(PROCESSING_SCHEDULES_ALERT_KEY);
};

const shouldShowProcessingAlert = (): boolean => {
  const timestamp = getProcessingAlertTimestamp();
  if (!timestamp) return false;

  const now = Date.now();
  const elapsed = now - timestamp;

  if (elapsed > ALERT_DURATION) {
    clearProcessingAlert();
    return false;
  }

  return true;
};

interface DeviceInfo {
  modelNumber: string;
  serialNumber: string;
  deviceId: string;
}

const ScheduleList = () => {
  const { uuid: patientId } = useParams() as { uuid: string };
  const [activeTab, setActiveTab] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [showProcessingAlert, setShowProcessingAlert] = useState(() => shouldShowProcessingAlert());
  const navigate = useNavigate();
  const getFilterParams = () => {
    switch (activeTab) {
      case 'completed':
        return { status: 'completed' as const };
      case 'scheduled':
        return { status: 'scheduled' as const };
      case 'no-show':
        return { status: 'no show' as const };
      default:
        return {};
    }
  };

  const { data, isLoading, isFetching, error, refetch } = useGetPatientSchedulesQuery(
    {
      patientId,
      limit: 10,
      page: currentPage,
      ...getFilterParams()
    },
    {
      refetchOnMountOrArgChange: true
    }
  );

  const { data: printData, isLoading: isPrintLoading } = useGetPatientSchedulesQuery(
    {
      patientId,
      limit: 10,
      page: currentPage,
      ...{ status: 'scheduled' as const }
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !isPrintModalOpen
    }
  );

  const { data: patient } = useGetPatientQuery(patientId, {
    refetchOnMountOrArgChange: true
  });

  const isPdfGenerationEnabled = patient?.clinic?.parent?.enableSchedulePdfGeneration ?? false;
  const { data: clinicData } = useGetOrgByIdQuery(patient?.clinic?.id ?? '', {
    skip: !patient?.clinic?.id
  });

  const isAppointmentManagedInRs =
    clinicData?.organization?.parent?.isAppointmentManagedInRs ??
    clinicData?.organization?.isAppointmentManagedInRs ??
    false;
  const devices = patient?.matchDevices;

  const schedules: PatientSchedule[] = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  const availableDevices: DeviceInfo[] =
    devices?.map((device: any) => ({
      deviceId: device.id,
      modelNumber: device.modelNumber || '',
      serialNumber: device.serialNumber || '',
      vendor: device.vendor || '',
      type: device.type || '',
      isEriTracked: device.isEriTracked ?? false
    })) ?? [];

  // Handle processing alert timer
  useEffect(() => {
    if (!showProcessingAlert) return;

    const timestamp = getProcessingAlertTimestamp();
    if (!timestamp) {
      setShowProcessingAlert(false);
      return;
    }

    const remainingTime = ALERT_DURATION - (Date.now() - timestamp);

    if (remainingTime <= 0) {
      setShowProcessingAlert(false);
      clearProcessingAlert();
      return;
    }

    const timeoutId = setTimeout(() => {
      setShowProcessingAlert(false);
      clearProcessingAlert();
    }, remainingTime);

    return () => clearTimeout(timeoutId);
  }, [showProcessingAlert]);

  const handleTabChange = (value: string | null) => {
    setActiveTab(value || 'all');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEditScheduleClick = () => {
    setIsModalOpen(true);
  };
  const handlePrintScheduleClick = () => {
    setIsPrintModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalSuccess = () => {
    // Set the processing alert timestamp and show the alert
    setProcessingAlertTimestamp();
    setShowProcessingAlert(true);
    refetch();
  };

  const handleAlertClose = () => {
    setShowProcessingAlert(false);
    clearProcessingAlert();
  };

  const getDeviceDisplayName = (device: { vendor: string; type: string }): string => {
    const vendorMap: Record<string, string> = {
      medtronic: 'MDT',
      abbott: 'ABT',
      biotronik: 'BIO',
      sorin: 'SOR'
    };

    const typeMap: Record<string, string> = {
      PM: 'PM',
      ICD: 'ICD',
      'CRT-P': 'CRT-P',
      'CRT-D': 'CRT-D'
    };

    const vendor = vendorMap[device.vendor.toLowerCase()] || device.vendor.toUpperCase();
    const type = typeMap[device.type] || device.type;

    return `${vendor} ${type}`;
  };

  if (error) {
    return (
      <Flex direction={'column'} gap={'md'}>
        <InfoMessage
          title={'Error Loading Schedules'}
          message={'Please try refreshing the page or contact support if the issue persists.'}
          c="red"
        />
      </Flex>
    );
  }

  const renderLoadingRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Table.Tr key={`loading-${index}`}>
        <Table.Td>
          <Skeleton height={16} width="60%" />
        </Table.Td>
        <Table.Td>
          <Skeleton height={16} width="80%" />
        </Table.Td>
        <Table.Td>
          <Skeleton height={16} width="70%" />
        </Table.Td>
        <Table.Td>
          <Flex direction="column" gap={4}>
            <Skeleton height={16} width="50%" />
            <Skeleton height={12} width="70%" />
          </Flex>
        </Table.Td>
      </Table.Tr>
    ));
  };

  const renderDataRows = () => {
    return schedules.map((schedule) => (
      <Table.Tr key={schedule.id}>
        <Table.Td>
          <Text size="sm" fw={500}>
            {schedule.scheduledDate ? formatDate(schedule.scheduledDate, 'MM/DD/YY') : 'Unassigned'}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{schedule.type}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{getDeviceDisplayName(schedule.device)}</Text>
        </Table.Td>

        <Table.Td>
          <Flex align="flex-start" gap={0} direction={'column'}>
            <Text size="sm" px={0}>
              {schedule.status}
            </Text>

            {schedule.isReceived && (
              <Text size="xs" c="dimmed" px={0}>
                (Eligible Tx Received)
              </Text>
            )}

            <Group gap="xs">
              {schedule.transmissionReportId && (
                <Text
                  size="xs"
                  px={0}
                  className={styles.viewReportLink}
                  onClick={() => {
                    if (schedule.transmissionReportId) {
                      navigate(
                        routes.physician.transmissionReportDetail(
                          schedule.transmissionReportId?.toString()
                        )
                      );
                    }
                  }}>
                  View Report <IconExternalLink size={12} />
                </Text>
              )}
            </Group>
          </Flex>
        </Table.Td>
      </Table.Tr>
    ));
  };

  return (
    <>
      <Flex direction={'column'}>
        <Flex className={styles.filterHeaderSection} justify={'space-between'} align={'center'}>
          <Tabs
            classNames={{
              tab: styles.filterTabs
            }}
            value={activeTab}
            onChange={handleTabChange}
            variant="pills">
            <Tabs.List>
              <Tabs.Tab value="all">All</Tabs.Tab>
              <Tabs.Tab value="scheduled">Scheduled</Tabs.Tab>
              <Tabs.Tab value="completed">Completed</Tabs.Tab>
              <Tabs.Tab value="no-show">No Show</Tabs.Tab>
            </Tabs.List>
          </Tabs>
          {isPdfGenerationEnabled ? (
            <Flex gap={20}>
              <Box mt={'3px'} onClick={handleEditScheduleClick} style={{ cursor: 'pointer' }}>
                <EditMenuIcon />
              </Box>
              <Box onClick={handlePrintScheduleClick} style={{ cursor: 'pointer' }}>
                <PrintIcon />
              </Box>
            </Flex>
          ) : (
            <Button
              size="xs"
              onClick={handleEditScheduleClick}
              variant="subtle"
              leftSection={<IconPlus size={16} />}>
              Edit Schedule
            </Button>
          )}
        </Flex>

        {showProcessingAlert && (
          <Alert
            variant="light"
            color="blue"
            title="Processing Schedule Changes"
            mt="md"
            icon={<IconInfoCircle />}
            withCloseButton
            onClose={handleAlertClose}>
            Schedule changes are being processed and may take around five minutes to appear. Please
            refresh the page or come back later to see the generated schedules.
          </Alert>
        )}

        <Flex direction={'column'} my={8}>
          <Table highlightOnHover stickyHeader>
            <Table.Thead>
              <Table.Tr>
                <Table.Th className={styles.tableHeading}>Date</Table.Th>
                <Table.Th className={styles.tableHeading}>Type</Table.Th>
                <Table.Th className={styles.tableHeading}>Device</Table.Th>
                <Table.Th className={styles.tableHeading}>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading || isFetching ? renderLoadingRows() : renderDataRows()}
            </Table.Tbody>
          </Table>

          {!isLoading && !isFetching && schedules.length === 0 && (
            <InfoMessage
              title={'No Schedules'}
              message={
                activeTab === 'all'
                  ? 'No schedules available for this patient.'
                  : `No ${activeTab} schedules found for this patient.`
              }
            />
          )}

          {!isLoading && !isFetching && schedules.length > 0 && (
            <Flex justify="center" mt="lg">
              <Pagination
                value={currentPage}
                onChange={handlePageChange}
                total={totalPages}
                size="sm"
              />
            </Flex>
          )}
        </Flex>
      </Flex>

      <PrintScheduleIndex
        patientName={`${patient?.familyName ?? ''} ${patient?.givenName ?? ''}`}
        address={patient?.address ?? ({} as Address)}
        modalOpen={isPrintModalOpen}
        setModalOpen={setIsPrintModalOpen}
        printData={printData}
        isLoading={isPrintLoading}
      />

      <DeviceScheduleModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        patientSFId={patientId}
        shouldShowDeviceSelector={true}
        availableDevices={availableDevices}
        isAppointmentManagedInRs={isAppointmentManagedInRs}
      />
    </>
  );
};

const ScheduleListWithBoundary = withErrorBoundary(ScheduleList, {
  fallback: (
    <InfoMessage
      title={'Failed To Load Schedules'}
      message={'Please refresh the page or contact support if the issue persists.'}
    />
  )
});

export default ScheduleListWithBoundary;
